import { AlertCircle } from 'lucide-react';
import { formatFullName } from 'shared/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { formatDate } from './format-date';
import type { EmailConflict } from './account-email-actions.hooks';
import type { ManagedUser } from './types';

interface ConflictAlertProps {
  conflict: EmailConflict;
  onRelease: (args: { email: string; holder: ManagedUser }) => void;
}

/**
 * Explains why an address could not be added: it belongs to a deleted account (offering to release
 * it when it is one of that account's secondaries), to an active account, or to an account that
 * could not be identified.
 *
 * @param props.onRelease - Called when support chooses to release the address from the deleted account.
 */
export function ConflictAlert({ conflict: { email, holder }, onRelease }: ConflictAlertProps) {
  if (!holder) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>This address already belongs to another account.</AlertDescription>
      </Alert>
    );
  }

  const holderName = formatFullName(holder);
  const releasable = holder.secondaryEmails.some(secondary => secondary.email === email);

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        {holder.isDeleted ? (
          <>
            <strong>{email}</strong> already belongs to a <strong>deleted</strong> account (
            {holderName}, deleted {holder.deletedAt ? formatDate(holder.deletedAt) : 'earlier'}).{' '}
            {releasable
              ? 'Release it from that account to add it here.'
              : 'It is that account’s primary email, so it cannot be released from here.'}
            {releasable && (
              <div className="mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onRelease({ email, holder })}>
                  Release and add here…
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <strong>{email}</strong> already belongs to another active account ({holderName}). Use
            Merge Users if these are the same person.
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
